import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { signIn } from '../services/auth';
import { useAuth } from '../store';

/**
 * Login Page
 * Allows users to sign in with email and password
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const returnTo = searchParams.get('returnTo');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate inputs
    if (!email.trim() || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    try {
      await signIn(email.trim(), password);
      
      // Refresh auth context
      await refreshUser();
      
      // Check for pending event join
      const pendingEventJoin = sessionStorage.getItem('pendingEventJoin');
      if (pendingEventJoin) {
        sessionStorage.removeItem('pendingEventJoin');
        // Extract event ID if it's a full URL
        let eventId = pendingEventJoin.trim();
        if (eventId.includes('/join/')) {
          const match = eventId.match(/\/join\/([^\/\s]+)/);
          if (match) {
            eventId = match[1];
          }
        }
        navigate(`/join/${eventId}`);
        return;
      }
      
      // Redirect to return URL or games page
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/games');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
      <Card padding="lg" className="w-full max-w-md">
        <h1 className="text-2xl font-bold text-primary-text mb-6 text-center">
          Sign In
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-primary-text mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="you@example.com"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-primary-text mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="••••••••"
              disabled={loading}
              required
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={loading}
            className="w-full"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>

          <div className="text-center space-y-2">
            <Link
              to="/forgot-password"
              className="text-sm text-primary hover:underline block"
            >
              Forgot password?
            </Link>
            
            <div className="text-sm text-secondary-text">
              Don't have an account?{' '}
              <Link
                to={`/signup${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
                className="text-primary hover:underline"
              >
                Sign up
              </Link>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}

